import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from '@SiteComponents/Button'
import Popup from '@SiteComponents/Popup'
import StepIndicator from '@SiteComponents/StepIndicator'
import Cancel from '@Images/new_project_popup_cancel.svg'

import {
  closeDashboardPopups,
  openSetupProjectPopup,
  openSetupProjectPopupTts,
} from '@SiteModules/Popup/actions'

import {
  createProject,
} from '@SiteModules/Project/actions'

import {
  getCurrentWorkspaceId,
} from '@SiteModules/Workspace/selectors'

import {
  getSetupProjectPopupIsOpen,
  getSetupProjectPopupTtsIsOpen,
} from '@SiteModules/Popup/selectors'

import './NewProjectPopup.scss'

const NewProjectPopup: React.FC = () => {
  const dispatch = useDispatch()

  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)
  const setupProjectPopupIsOpen = useSelector(getSetupProjectPopupIsOpen)
  const setupProjectPopupTtsIsOpen = useSelector(getSetupProjectPopupTtsIsOpen)

  const handleClose = () => {
    dispatch(closeDashboardPopups())
  }

  const setSelectedOptionRepurpose = () => {
    dispatch(openSetupProjectPopup({ projectVersionIds: [], projectType: 'repurpose' }))
  }

  const setSelectedOptionSubtitles = () => {
    dispatch(openSetupProjectPopup({ projectVersionIds: [], projectType: 'subtitles' }))
  }

  const setSelectedOptionDubbing = () => {
    dispatch(openSetupProjectPopup({ projectVersionIds: [], projectType: 'dubbing' }))
  }

  const setSelectedOptionTts = () => {
    dispatch(openSetupProjectPopupTts({ projectVersionIds: [] }))
  }

  const setSelectedOptionBlank = () => {
    dispatch(createProject({ workspaceId: currentWorkspaceId }))
  }

  const renderOptions = () => {
    return (
      <div className="NewProjectPopup__options">
        <div
          className="NewProjectPopup__option"
          onClick={setSelectedOptionRepurpose}
        >
          <div className="NewProjectPopup__option-header">
            ✨ Content repurposing

            <div className="NewProjectPopup__option-header-tag">
              New
            </div>
          </div>
          <div className="NewProjectPopup__option-subheader">
            Extract the best of your content: turn one video into viral clips,  natively adapted for every screen size and language.
          </div>
        </div>
        <div
          className="NewProjectPopup__option"
          data-testid="project-template-subtitles"
          onClick={setSelectedOptionSubtitles}
        >
          <div className="NewProjectPopup__option-header">
            Generate subtitles
          </div>
          <div className="NewProjectPopup__option-subheader">
            Automatically generate accurate subtitles from your video or audio. Perfect for improving accessibility, engagement, and translating your content.
          </div>
        </div>
        <div
          className="NewProjectPopup__option"
          data-testid="project-template-dubbing"
          onClick={setSelectedOptionDubbing}
        >
          <div className="NewProjectPopup__option-header">
            Generate dubbing
          </div>
          <div className="NewProjectPopup__option-subheader">
            Instantly translate and dub your video into multiple languages with AI. No studio, no voice actor, just global reach in minutes.
          </div>
        </div>
        <div
          className="NewProjectPopup__option"
          onClick={setSelectedOptionTts}
        >
          <div className="NewProjectPopup__option-header">
            Generate voice-over
          </div>
          <div className="NewProjectPopup__option-subheader">
            Easily turn your text into professional voice-overs in seconds. Perfect for YouTube, marketing videos or tutorials.
          </div>
        </div>
        <div
          className="NewProjectPopup__option"
          onClick={setSelectedOptionBlank}
        >
          <div className="NewProjectPopup__option-header">
            Edit a video
          </div>
          <div className="NewProjectPopup__option-subheader">
            Fine-tune your audio or video directly in our editor. Trim, adjust and enhance your content before publishing.
          </div>
        </div>
      </div>
    )
  }

  const renderBottom = () => {
    return (
      <div className="NewProjectPopup__bottom">
        <Button
          caption="Cancel"
          icon={<Cancel />}
          onClick={handleClose}
          styleType="gray-subtle"
          size="small"
        />
        <div className="NewProjectPopup__bottom-right">
          <StepIndicator
            currentStep={1}
            maxSteps={2}
            stepName="Type"
          />
        </div>
      </div>
    )
  }

  return (
    <Popup
      className="NewProjectPopup"
      zIndex={100000}
      closePopup={handleClose}
      shouldNotRenderContent={setupProjectPopupIsOpen || setupProjectPopupTtsIsOpen}
    >
      <div className="NewProjectPopup__header">
        Which project type do you want to create?
      </div>
      <div className="NewProjectPopup__subheader">
        Choose a template
      </div>
      {renderOptions()}
      {renderBottom()}
    </Popup>
  )
}

export default NewProjectPopup
